import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react'
import { useAppDispatch } from '@/store'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { createGroup } from '@/store/slices/groupsSlice'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export default function CreateGroupModal({ isOpen, onClose }: Props) {
  const { t } = useAppTranslation(['admin', 'common'])
  const dispatch = useAppDispatch()
  const toast = useToast()
  const [name, setName] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return
    setIsLoading(true)
    try {
      await dispatch(createGroup({ name: name.trim() })).unwrap()
      toast({
        title: t('groups.createSuccess', { ns: 'admin' }),
        status: 'success',
        duration: 2000,
      })
      setName('')
      onClose()
    } catch {
      toast({ title: t('groups.createFailed', { ns: 'admin' }), status: 'error', duration: 3000 })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setName('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('groups.createGroup', { ns: 'admin' })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>{t('groups.groupName', { ns: 'admin' })}</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('groups.groupNamePlaceholder', { ns: 'admin' })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate()
              }}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button colorScheme="blue" onClick={handleCreate} isLoading={isLoading}>
            {t('groups.createGroup', { ns: 'admin' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
